import React from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Globe } from "lucide-react";
import EventomLogo from "@/components/EventomLogo";
import { SocialRow } from "@/components/SocialCTA";

/**
 * Footer — locked to the Iter 39 brief:
 *   Row 1: 6 primary pages (matches header exactly)
 *   Row 2: 5 policy + Work-With-Eventom links
 *   Tagline: AI-Powered Live Entertainment Platform for Bharat
 *   Copyright: 2025 Eventom. All rights reserved.
 */
const PRIMARY_LINKS = [
  { to: "/", label: "Home", testId: "footer-home" },
  { to: "/events", label: "Events", testId: "footer-events" },
  { to: "/contests", label: "Contests", testId: "footer-contests" },
  { to: "/ai-gallery", label: "AI Photo Gallery", testId: "footer-ai-gallery" },
  { to: "/clubvip", label: "Club VIP", testId: "footer-clubvip" },
  { to: "/ecosystem", label: "Grow Your Business", testId: "footer-grow-biz" },
];

const POLICY_LINKS = [
  { to: "/policies/privacy", label: "Privacy Policy", testId: "footer-privacy" },
  { to: "/policies/terms", label: "Terms of Service", testId: "footer-terms" },
  { to: "/policies/refund", label: "Refund Policy", testId: "footer-refund" },
  { to: "/policies/cancellation", label: "Cancellation Policy", testId: "footer-cancellation" },
  { to: "/work-with-eventom", label: "Work With Eventom", testId: "footer-work" },
];

export default function Footer() {
  return (
    <footer className="mt-24 border-t-2 border-line bg-ink text-white">
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-1">
            <EventomLogo height={36} />
            <div className="mt-4 chip bg-lime/10 text-lime !text-[10px]">AI-Powered Live Entertainment Platform for Bharat</div>
            <div className="mt-6">
              <a href="https://wa.me/917250120666" target="_blank" rel="noreferrer" data-testid="footer-whatsapp" className="btn-lime !py-2 !px-4 !text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp Sales
              </a>
            </div>
            <div className="mt-4">
              <a href="https://instagram.com/eventom.online" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="icon-circle bg-white/5 hover:bg-magenta text-white"><Instagram className="w-4 h-4" /></a>
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-lime mb-4">Explore</div>
            <ul className="space-y-3 text-sm">
              {PRIMARY_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} data-testid={l.testId} className="text-muted hover:text-white transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-magenta mb-4">Legal &amp; Work</div>
            <ul className="space-y-3 text-sm">
              {POLICY_LINKS.map((l) => (
                <li key={l.to}><Link to={l.to} data-testid={l.testId} className="text-muted hover:text-white transition-colors">{l.label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/5">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-lime mb-4">Join the crowd</div>
          <SocialRow />
        </div>
      </div>

      <div className="border-t border-white/5 py-5 px-6 max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 text-xs font-mono">
        <div className="flex flex-wrap items-center gap-3 text-muted">
          <span className="inline-flex items-center gap-2"><Globe className="w-3 h-3 text-lime" /> eventom.online</span>
          <span data-testid="footer-copyright">Eventom Corptech Private Limited © 2026. All rights reserved</span>
        </div>
      </div>
    </footer>
  );
}
